import { t } from '../i18n';
import React from 'react';

/**
 * 根级错误边界：任何子组件渲染抛异常时，展示可读的错误页而不是整屏白屏。
 *
 * 重要：兜底 UI 必须是【纯 HTML / 零第三方 UI 库依赖】。
 * 之前用 antd 组件做兜底，一旦崩溃本身与 antd（context / 版本 / hooks）相关，
 * 兜底 UI 会二次崩溃，导致 React 卸载整棵树 → #root 变空 → 白屏且看不到任何错误。
 * 用原生元素后，无论什么错误都能稳定显示错误文本，便于定位。
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary] Uncaught render error:', error, info && info.componentStack);
        // 同时挂到全局，方便远程排查
        try { window.__LAST_RENDER_ERROR__ = { message: error && error.message, stack: error && error.stack, componentStack: info && info.componentStack }; } catch (e) { /* noop */ }
        this.setState({ info });
    }

    // 导航时重置错误 —— 但**不 remount children**。
    // 之前外层用 key={location.pathname} 达到"换页清错误"，副作用是每次 URL 变(含 tab 切换)
    // 都卸载并重挂整棵子树 → 详情页 loading 重置为 true → 中间区整页 loading。改为传 resetKeys:
    // 仅在【已处于错误态】且 resetKeys 变化时清错误;正常导航子树保持挂载,tab 切换丝滑。
    componentDidUpdate(prevProps) {
        if (this.state.hasError) {
            const prev = prevProps.resetKeys || [];
            const cur = this.props.resetKeys || [];
            const changed = cur.length !== prev.length || cur.some((k, i) => k !== prev[i]);
            if (changed) {
                this.setState({ hasError: false, error: null, info: null });
            }
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        const { error, info } = this.state;
        const message = (error && (error.message || String(error))) || t('Unknown error');
        const detail = ((error && error.stack) ? error.stack + '\n\n' : '') + ((info && info.componentStack) ? t('Component stack:{{stack}}', { stack: info.componentStack }) : '');

        const wrap = { minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: '#f5f5f5', padding: '48px 16px', boxSizing: 'border-box' };
        const card = { width: '100%', maxWidth: 820, background: '#fff', border: '1px solid #ffccc7', borderRadius: 10, padding: 24, fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
        const btn = { display: 'inline-block', padding: '6px 16px', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff', cursor: 'pointer', fontSize: 14, marginRight: 8 };
        const btnPrimary = { ...btn, background: '#1677ff', color: '#fff', border: '1px solid #1677ff' };

        return (
            <div style={wrap}>
                <div style={card}>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#cf1322', marginBottom: 8 }}>{t('Something went wrong on this page')}</div>
                    <div style={{ color: '#595959', marginBottom: 16, fontSize: 14 }}>{t('An error occurred while rendering this page. Try refreshing. If the issue persists, send us a screenshot of the error details below.')}</div>
                    <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '10px 12px', color: '#a8071a', fontWeight: 600, fontSize: 14, wordBreak: 'break-word', marginBottom: 16 }}>{message}</div>
                    <div style={{ marginBottom: 16 }}>
                        <button style={btnPrimary} onClick={() => window.location.reload()}>{t('Refresh page')}</button>
                        <button style={btn} onClick={() => { window.history.length > 1 ? window.history.back() : (window.location.href = '/'); }}>{t('Back')}</button>
                    </div>
                    {detail ? (
                        <details>
                            <summary style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: 13 }}>{t('Error details (technical information)')}</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#8c8c8c', maxHeight: 360, overflow: 'auto', marginTop: 8, background: '#fafafa', padding: 12, borderRadius: 6 }}>{detail}</pre>
                        </details>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
